import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';
import type { WorkflowRule } from '../../lib/workflows/rules';
import { RulesEditor, type RuleGroup } from './Workflows';

/**
 * The rules library: reusable groups of post-processing rules.
 *
 * A group is copied BY VALUE into a workflow, so nothing here is a live dependency — editing a group does not
 * change any workflow that already took it, and deleting one leaves them working. That is deliberate: a rule
 * changing under a workflow's feet is the last thing a library should introduce.
 */

export const ruleGroupPath = (groupId: string) => `/app/admin/rule-library/${encodeURIComponent(groupId)}`;

export function RuleLibrary() {
  const isAdmin = useIsSiteAdmin();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['rule-groups'], queryFn: () => api.get<RuleGroup[]>('/admin/rule-groups'), enabled: isAdmin });

  if (!isAdmin) return <div className="panel empty"><h3>Restricted</h3><p>The rules library is available to Lakota platform staff only.</p></div>;

  const rows = list.data ?? [];
  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Rules library</h1>
          <div className="sub">Reusable groups of post-processing rules. A workflow copies a group; later edits here don't change it.</div>
        </div>
        <Link className="btn btn-primary" to={ruleGroupPath('new')}>+ New group</Link>
      </div>

      <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
        {list.isLoading ? <p className="loading-screen">// loading…</p> : rows.length === 0 ? (
          <p className="cart-note" style={{ textAlign: 'left', padding: 16 }}>
            // the library is empty — build rules on a workflow, then save them here from its rules panel
          </p>
        ) : (
          <table className="tx-table">
            <thead><tr><th>Group</th><th>Rules</th><th></th></tr></thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.groupId} className="row-click" onClick={() => navigate(ruleGroupPath(g.groupId))}>
                  <td>
                    {g.name}
                    <div className="acct-code">{g.groupId}</div>
                    {g.description && <div className="field-help" style={{ marginTop: 4 }}>{g.description}</div>}
                  </td>
                  <td>{g.rules?.length ?? 0}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Link className="track-link" to={ruleGroupPath(g.groupId)} onClick={(e) => e.stopPropagation()}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function RuleGroupDetailPage() {
  const { groupId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isAdmin = useIsSiteAdmin();
  const list = useQuery({ queryKey: ['rule-groups'], queryFn: () => api.get<RuleGroup[]>('/admin/rule-groups'), enabled: isAdmin });

  const isNew = groupId === 'new';
  const found = (list.data ?? []).find((g) => g.groupId === groupId);
  /*
   * Local draft, seeded once. Editing a group must not write on every keystroke — the library is what other
   * workflows copy from, and half-typed rules are not something to publish.
   */
  const [draft, setDraft] = useState<RuleGroup | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const current = draft ?? (isNew ? { groupId: '', name: '', rules: [] as WorkflowRule[] } : found);

  const save = useMutation({
    mutationFn: (g: RuleGroup) => api.put<RuleGroup>(`/admin/rule-groups/${encodeURIComponent(g.groupId)}`, {
      name: g.name, description: g.description, rules: g.rules,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rule-groups'] }); navigate('/app/admin/rule-library'); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not save the group'),
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/admin/rule-groups/${encodeURIComponent(groupId)}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rule-groups'] }); navigate('/app/admin/rule-library'); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not delete the group'),
  });

  if (!isAdmin) return <div className="panel empty"><h3>Restricted</h3><p>The rules library is available to Lakota platform staff only.</p></div>;
  if (list.isLoading) return <p className="loading-screen">// loading group…</p>;
  if (!current) {
    return (
      <div className="panel empty">
        <h3>Unknown group</h3>
        <p><Link to="/app/admin/rule-library" className="track-link">← Rules library</Link></p>
      </div>
    );
  }

  const set = (patch: Partial<RuleGroup>) => setDraft({ ...current, ...patch });
  /* The id is the storage key, so it is fixed after creation — renaming it would orphan the group. */
  const canSave = Boolean(current.groupId.trim() && current.name.trim() && current.rules.length);

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>{isNew ? 'New rule group' : current.name || current.groupId}</h1>
          <div className="sub">
            <Link to="/app/admin/rule-library" className="track-link">← Rules library</Link>
            {!isNew && <>{' · '}<span className="mono">{current.groupId}</span></>}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="demo-grid">
          <label className="field">
            <span className="field-label">Id</span>
            <input className="input mono" value={current.groupId} disabled={!isNew}
              placeholder="e.g. format-name"
              onChange={(e) => set({ groupId: e.target.value.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-') })} />
            <span className="field-help">{isNew ? 'Lower-case, used as the key. Cannot be changed later.' : 'Fixed — it is how workflows found this group.'}</span>
          </label>
          <label className="field" style={{ gridColumn: '2 / -1' }}>
            <span className="field-label">Name</span>
            <input className="input" value={current.name} placeholder="Format Name"
              onChange={(e) => set({ name: e.target.value })} />
          </label>
          <label className="field" style={{ gridColumn: '1 / -1' }}>
            <span className="field-label">Description</span>
            <textarea className="input field-multiline" rows={2} value={current.description ?? ''}
              placeholder="What this group does, and anything a newcomer would not guess."
              onChange={(e) => set({ description: e.target.value })} />
            {/* The five name rules only make sense together; a description is where that gets said. */}
            <span className="field-help">Worth writing: a group of rules that depend on each other is hard to read cold.</span>
          </label>
        </div>
      </div>

      <div className="panel">
        <div className="section-title" style={{ marginTop: 0 }}>Rules</div>
        {/* The same editor a workflow uses, preview included — a group should be provable before it is published. */}
        <RulesEditor rules={current.rules} onChange={(rules) => set({ rules })} fieldNames={[]} />
      </div>

      {err && <p className="cart-error">{err}</p>}
      <div className="flow-actions">
        {!isNew && (
          <button className="btn btn-ghost" disabled={remove.isPending}
            onClick={() => remove.mutate()}>
            {remove.isPending ? 'Deleting…' : 'Delete group'}
          </button>
        )}
        <button className="btn btn-primary" disabled={!canSave || save.isPending}
          onClick={() => save.mutate(current)}>
          {save.isPending ? 'Saving…' : 'Save group'}
        </button>
      </div>
      {!canSave && <p className="field-help" style={{ textAlign: 'right' }}>An id, a name and at least one rule are needed.</p>}
    </div>
  );
}
